import type { ParamMap, Params } from '@angular/router';
import type { CreditDashboardFiltre } from '../interfaces/credit.interface';

/**
 * Filtres de liste portés par l'URL.
 *
 * Les écrans de dashboard conservaient leurs filtres en mémoire : revenir sur la
 * liste depuis une demande repartait donc d'une page vierge. En les écrivant dans
 * les query params, le retour navigateur, le rechargement et le partage de lien
 * restituent la même vue — et le resolver peut charger directement la bonne page.
 *
 * Les paramètres vides ne sont pas écrits, pour garder des URLs lisibles.
 */

export interface EtatListeUrl {
  filtre: CreditDashboardFiltre;
  page: number;
  perPage: number;
}

export function etatListeDepuisParams(params: ParamMap, perPageParDefaut = 10): EtatListeUrl {
  const nombre = (cle: string, defaut: number) => {
    const valeur = Number(params.get(cle));
    return Number.isFinite(valeur) && valeur > 0 ? valeur : defaut;
  };
  const texte = (cle: string) => params.get(cle) || undefined;
  const entier = (cle: string) => {
    const valeur = Number(params.get(cle));
    return params.get(cle) && Number.isFinite(valeur) ? valeur : null;
  };

  return {
    filtre: {
      agence: texte('agence'),
      typeCredit: entier('typeCredit'),
      statut: entier('statut'),
      codeClient: texte('codeClient'),
      dateDebut: texte('dateDebut'),
      dateFin: texte('dateFin'),
    },
    page: nombre('page', 1),
    perPage: nombre('perPage', perPageParDefaut),
  };
}

/** Vrai dès qu'un critère est renseigné : sert à choisir entre chargement et filtrage. */
export function filtreActif(filtre: CreditDashboardFiltre): boolean {
  return Object.values(filtre).some((valeur) => valeur !== null && valeur !== undefined && valeur !== '');
}

/**
 * `null`/`undefined`/`''` effacent le paramètre plutôt que d'écrire une valeur
 * vide ; page et taille par défaut ne sont pas écrites non plus, pour garder des
 * URLs lisibles.
 */
export function paramsDepuisEtat(etat: EtatListeUrl, perPageParDefaut = 10): Params {
  const params: Params = {
    ...etat.filtre,
    page: etat.page > 1 ? etat.page : null,
    perPage: etat.perPage === perPageParDefaut ? null : etat.perPage,
  };
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur === undefined || valeur === '') params[cle] = null;
  }
  return params;
}
